import React, {useEffect, useState} from 'react';
import { Box } from '@mui/material';

import TextareaAutosize from '@mui/material/TextareaAutosize';
import CustomFileCard from './CustomFileCard';
import {FILE_TYPE, getFileIcon, getFileNameWithoutExtension} from '../Helpers/fileType';
import {downloadLocalFile, formatBytes} from '../Apis/functions';
import { IconSend } from './buttons/IconButtons';
import ButtonAction from './buttons/ButtonAction';
import CustomFileUploadClip from "./FileUpload/CustomFileUploadClip";
import {CustomStyleFormHelperText, CustomStyleTextFile, CustomStyleTextFileRequired} from "./Styles";
import CustomTextFieldApp from "./CustomTextField/CustomTextFieldApp";

/**
 * CustomTextFile Component
 *
 * A reusable component that allows the user to write a text comment
 * and optionally attach a file. It renders a textarea, file upload control,
 * file preview card, and a send action button.
 *
 * @author Susana Garcia Rodriguez
 * @jobTitle Frontend Developer - IBL
 * @email sgarcia@ibl.mx
 *
 * @param {Object} props Component props.
 * @param {Function} props.onSend Callback triggered when sending the message.
 * Receives an object containing:
 *  - text {string}: Written comment.
 *  - file {File|null}: Attached file or null.
 * @param {string} props.id Unique identifier for the textarea.
 * @param {string} [props.counterColor='#707070'] Color used for the character counter.
 * @param {string} [props.type='text'] Input type.
 * @param {number} [props.length=60] Maximum allowed characters.
 * @param {boolean} [props.countDecimals=false] Enables decimal validation.
 * @param {boolean} [props.disabled=false] Disables input and counter.
 * @param {boolean} [props.showCounter=true] Controls character counter visibility.
 * @param {string} [props.helperText=''] Helper text key for translations.
 * @param {boolean} props.required Indicates whether the field is required.
 * @param {boolean} props.emptyFields Flags empty field validation.
 * @param {boolean} [props.isValid=true] Indicates whether the input is valid.
 * @param {string} [props.isValidText='format_invalid'] Translation key for invalid format message.
 * @param {boolean} props.clear Triggers input and file reset.
 * @param {Function} props.setClear Setter to reset clear flag.
 *
 * @returns {JSX.Element} Custom text and file input component.
 */
const CustomTextFile = ({
    onSend,
    id,
    counterColor = '#707070',
    type = "text",
    length = 60,
    countDecimals = false,
    disabled=false,
    showCounter = true,
    helperText = '',
    required,
    emptyFields,
    emptyFieldsText='requiredField',
    isValid = true,
    isValidText = 'format_invalid',
    clear,
    setClear
}) => {
    const [value, setValue] = useState('');
    const [file, setFile] = useState(null);

    /**
     * Clears text and file when clear flag changes.
     */
    useEffect(() => {
        if(clear){
            setValue("")
            handleClearFile();
            setClear(false);
        }
    }, [clear]);

    /**
     * Sends the text and file data to the parent component.
     */
    const send = () => {
        if (!onSend) return;
        onSend({ text: value, file: file });
    };

    /**
     * Removes the currently attached file.
     */
    const handleClearFile = () => {
        setFile(null);
    };

    /**
     * Sets the selected file.
     *
     * @param {File} e Selected file.
     */
    const handleFile = (e) => {
        setFile(e)
    };

    /**
     * Handles textarea value change.
     *
     * @param {React.ChangeEvent<HTMLTextAreaElement>} e Change event.
     */
    const onChange = (e) => {
        setValue(e.target.value)
    }

    const {
        t,
        handleKeyDown,
        handlePaste,
        CharacterCount
    } = CustomTextFieldApp({
        type,
        length,
        countDecimals,
        onChange
    });


    return (
        <>
            <Box className={`p-3 border-radius-8px d-flex flex-column gap-2`}
                sx={(emptyFields && (value === '' || value === null)) || (!isValid) ? CustomStyleTextFileRequired : CustomStyleTextFile}
            >
                <TextareaAutosize
                    id={id}
                    placeholder={t('leave_a_comment')}
                    value={value}
                    className='form-control border-0 textarea-box-shadow'
                    onChange={onChange}
                    required={required}
                    error={(emptyFields && (value === '' || value === null)) || (!isValid)}
                    onKeyDown={handleKeyDown}
                    onPaste={handlePaste}
                    maxLength={length}
                />
                {file && (
                    <div className='mt-3'>
                        <CustomFileCard
                            Icon={getFileIcon(file)}
                            title={getFileNameWithoutExtension(file.name)}
                            subtitle={formatBytes(file.size)}
                            fileValue={file}
                            buttonId='btn_ctf_preview_file'
                            onClick={() => downloadLocalFile(file)}
                            showBtnDelete={true}
                            showBtnDownload={false}
                            clickBtnDelete={handleClearFile}
                        />
                    </div>
                )}
                <div className='d-flex justify-content-between align-items-center'>
                    <CustomFileUploadClip
                        id={"fu_ctf_file"}
                        accept={{ ...FILE_TYPE.IMAGE_FILES_VIDEOS }}
                        handleFile={handleFile}
                        clear={clear}
                        setClear={setClear}
                    />
                    <ButtonAction id='btn_ctf_send' name='send' className='btn-icon-add' Icon={IconSend} onClick={send} />
                </div>
            </Box>
            { !emptyFields && helperText !== '' && (value === '' || value === null) ?
                <Box className={((emptyFields && (value === '' || value === null)) || (!isValid)) ? 'Mui-error': ''} sx={CustomStyleFormHelperText} disabled={disabled}>{t(helperText)}</Box>
                :
                <CharacterCount
                    disabled={disabled}
                    count={value?.toString()?.length || 0}
                    counterColor={counterColor}
                    helperText={emptyFields && (value === '' || value === null) ? t(emptyFieldsText) : !isValid ? t(isValidText) : ''}
                    max={length}
                    multiline={true}
                    showCounter={showCounter}
                    errorClass={((emptyFields && (value === '' || value === null)) || (!isValid)) ? 'Mui-error': ''}
                />
            }
        </>
    );
};

export default CustomTextFile;
